import type React from 'react';

import { Avatar } from '@change/ds-core-ui/Avatar';
import { Button } from '@change/ds-core-ui/Button';
import { Modal, ModalBody, ModalHeader } from '@change/ds-core-ui/Modal';
import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionStarterInfoTracking, usePetitionStarterPhoto } from '#src/app/pages/petitionDetails/shared/hooks';

type Petition = PetitionDetailsPageData['petition'];

type Props = {
	user: Petition['user'];
	organization: Petition['organization'];
	createdAt: Petition['createdAt'];
	createdAtLocalized: Petition['createdAtLocalized'];
	petitionPageStarterInHeaderEnabled: boolean;
};

type StarterClusterProps = {
	starterPhotoUrl?: string;
	displayName: string;
	createdAtLabel?: string;
	underlineName?: boolean;
};

const StarterCluster: React.FC<StarterClusterProps> = ({
	starterPhotoUrl,
	displayName,
	createdAtLabel,
	underlineName,
}) => (
	<span className="flex min-w-0 items-center gap-2">
		{starterPhotoUrl && (
			<span className="shrink-0">
				<Avatar size="small" src={starterPhotoUrl} alt="" />
			</span>
		)}
		<span className={cn('flex min-w-0 flex-col items-start', { 'gap-0.5': !underlineName })}>
			<Text
				variant="body"
				size="small"
				fontWeight="bold"
				truncate
				underline={underlineName}
				className={cn('text-typography-inverse-strong-fixed max-w-full', { 'leading-5': !underlineName })}
			>
				{displayName}
			</Text>
			{createdAtLabel && (
				<Text
					variant="body"
					size="small"
					className={cn('text-typography-inverse-strong-fixed/60', { 'leading-6': underlineName })}
				>
					<Translate
						value="corgi.petition_gamma.petition_starter_in_header.created"
						replacements={{ date: createdAtLabel }}
					/>
				</Text>
			)}
		</span>
	</span>
);

const TitleComponent: React.FC<{ displayName: string }> = ({ displayName }) => (
	<Text variant="heading" size="x-small" fontWeight="bold" className="line-clamp-2">
		{displayName}
	</Text>
);

export const PetitionStarterInHeaderMobile: React.FC<Props> = ({
	user,
	organization,
	createdAt,
	createdAtLocalized,
	petitionPageStarterInHeaderEnabled,
}) => {
	const { photoUrl: starterPhotoUrl } = usePetitionStarterPhoto();
	const { translate, localizeDate } = useI18n();
	const { trackStarterInfoClick } = usePetitionStarterInfoTracking();

	if (!petitionPageStarterInHeaderEnabled) return null;
	if (organization) return null;
	if (user.anonymous) return null;

	const createdAtLabel = createdAtLocalized || (createdAt ? localizeDate(createdAt, 'date', 'long') : undefined);

	const trigger = (
		<Button
			variant="tertiary"
			size="small"
			data-qa="petition-starter-in-header-mobile-trigger"
			data-testid="petition-starter-in-header-mobile-trigger"
			className="text-typography-inverse-strong-fixed! flex items-center bg-transparent! p-0! no-underline! hover:bg-transparent!"
			onClick={trackStarterInfoClick}
		>
			<StarterCluster
				starterPhotoUrl={starterPhotoUrl}
				displayName={user.displayName}
				createdAtLabel={createdAtLabel}
				underlineName
			/>
		</Button>
	);

	const cluster = user.description ? (
		<Modal
			size="small"
			// ds-core-ui Modal always applies min-h-[300px] (its footer check is always true); let the sheet hug its content until CHANGE-100167 is fixed
			className="[&>*]:min-h-0!"
			onClose={() => {}}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			trigger={trigger}
		>
			<ModalHeader
				title={<TitleComponent displayName={user.displayName} />}
				leadingSlot={
					starterPhotoUrl ? <Avatar size="medium" src={starterPhotoUrl} alt={user.displayName} /> : undefined
				}
			/>
			<ModalBody>
				<Text variant="body" className="text-typography-weak">
					{user.description}
				</Text>
			</ModalBody>
		</Modal>
	) : (
		<StarterCluster starterPhotoUrl={starterPhotoUrl} displayName={user.displayName} createdAtLabel={createdAtLabel} />
	);

	return (
		<div className="flex items-start pt-2" data-qa="petition-starter-in-header-mobile">
			{cluster}
		</div>
	);
};
